import './App.css';
import React, { useEffect, useState } from 'react'
import GuitarApp from './fretboard.js'
import DescriptionBox from './components/DescriptionBox.js'
import WaveformDisplay from './components/WaveformDisplay.js'
import SoundLevelBar from './components/SoundLevelBar.js'
import AudioDeviceSelector from './components/AudioDeviceSelector.js'
import FFTScopeDisplay from './components/FFTScopeDisplay.js'
import DeviceDetectionLog from './components/DeviceDetectionLog.js'
import QRCodeSection from './components/QRCodeSection.js'
import AudioDetection from './audioDetection.js'
import { canvasToBlobWithTimeout } from './utils/canvasUtils.js'


//https://learningprogramming.net/modern-web/react-functional-components/use-router-in-react-functional-components/

//flex
//https://www.youtube.com/watch?v=phWxA89Dy94

// Cookie helper functions
function setCookie(name, value, days = 365) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

// Calculate contrast color based on background luminance
function getContrastColor(hexColor) {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

// Timeout duration for copy status message
const COPY_STATUS_TIMEOUT = 2000; // milliseconds

export default function App() {

  const canvasRef = React.useRef(null);
  const audioDetectionRef = React.useRef(null);
 
  const [key, setKey] = useState("C");
  const [selScale, setSelScale] = useState('majorChord(CAGED)');
  const [notesOrNumbers, setNotesOrNumbers] = useState('intervals');
  const [sharpsOrFlats, setsharpsOrFlats] = useState('#');
  const [liveMode, setLiveMode] = useState(false);
  const [detectedNotes, setDetectedNotes] = useState([]);
  const [frequencyData, setFrequencyData] = useState(null);
  const [waveformData, setWaveformData] = useState([]);
  const [fftData, setFftData] = useState([]);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [audioDeviceLabel, setAudioDeviceLabel] = useState(null);
  const [fifthValidation, setFifthValidation] = useState(false);
  const [detectionMode, setDetectionMode] = useState('poly');
  const [enablePreFiltering, setEnablePreFiltering] = useState(true);
  const [enableHPS, setEnableHPS] = useState(true);
  const [enableSubharmonicCheck, setEnableSubharmonicCheck] = useState(true);
  const [enableAdaptiveThreshold, setEnableAdaptiveThreshold] = useState(true);
  const [enableStrictHarmonics, setEnableStrictHarmonics] = useState(false);
  const [requiredHarmonicsCount, setRequiredHarmonicsCount] = useState(2);
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [backgroundColor, setBackgroundColor] = useState(() => {
    // Load background color from cookie, default to olive/gold
    return getCookie('backgroundColor') || '#9c9438';
  });
  const [textColor, setTextColor] = useState(() => {
    // Calculate initial text color based on background
    const bgColor = getCookie('backgroundColor') || '#9c9438';
    return getContrastColor(bgColor);
  });
  const [copyStatus, setCopyStatus] = useState('');
  
  // Apply background color and text color to body
  useEffect(() => {
    document.body.style.backgroundColor = backgroundColor;
    const newTextColor = getContrastColor(backgroundColor);
    setTextColor(newTextColor);
    document.body.style.color = newTextColor;
    setCookie('backgroundColor', backgroundColor);
  }, [backgroundColor]);

  // Handle Live mode toggle
  const handleLiveModeToggle = async () => {
    if (!liveMode) {
      // Starting Live mode
      if (!AudioDetection.isSupported()) {
        alert('Audio detection is not supported in your browser. Please use a modern browser with microphone access.');
        return;
      }
      
      // Clear device logs when starting
      setDeviceLogs([]);
      
      try {
        const audioDetection = new AudioDetection(
          (notes) => {
            setDetectedNotes(notes);
          },
          (waveform) => {
            setWaveformData(waveform);
          },
          (volume) => {
            setVolumeLevel(volume);
          },
          (deviceLabel) => {
            setAudioDeviceLabel(deviceLabel);
          },
          (freqData) => {
            setFrequencyData(freqData);
          },
          (fftData) => {
            setFftData(fftData);
          },
          (logMessage) => {
            setDeviceLogs(prevLogs => [...prevLogs, logMessage]);
          }
        );
        
        // Initialize all detection settings from state
        audioDetection.setDetectionMode(detectionMode);
        audioDetection.setFifthValidation(fifthValidation);
        audioDetection.setPreFiltering(enablePreFiltering);
        audioDetection.setHPS(enableHPS);
        audioDetection.setSubharmonicCheck(enableSubharmonicCheck);
        audioDetection.setAdaptiveThreshold(enableAdaptiveThreshold);
        audioDetection.setStrictHarmonics(enableStrictHarmonics);
        audioDetection.setRequiredHarmonicsCount(requiredHarmonicsCount);
        
        await audioDetection.start();
        audioDetectionRef.current = audioDetection;
        setLiveMode(true);
      } catch (error) {
        alert('Could not access microphone. Please grant microphone permissions and try again.');
        console.error('Error starting audio detection:', error);
      }
    } else {
      // Stopping Live mode
      if (audioDetectionRef.current) {
        audioDetectionRef.current.stop();
        audioDetectionRef.current = null;
      }
      setLiveMode(false);
      setDetectedNotes([]);
      setFrequencyData(null);
      setWaveformData([]);
      setFftData([]);
      setVolumeLevel(0);
      setAudioDeviceLabel(null);
    }
  };

  // Handle fifth validation toggle
  const handleFifthValidationToggle = () => {
    if (audioDetectionRef.current) {
      const newValue = !fifthValidation;
      audioDetectionRef.current.setFifthValidation(newValue);
      setFifthValidation(newValue);
    }
  };

  // Handle detection mode toggle
  const handleDetectionModeToggle = () => {
    if (audioDetectionRef.current) {
      const newMode = detectionMode === 'mono' ? 'poly' : 'mono';
      audioDetectionRef.current.setDetectionMode(newMode);
      setDetectionMode(newMode);
    }
  };

  // Handle pre-filtering toggle
  const handlePreFilteringToggle = () => {
    if (audioDetectionRef.current) {
      const newValue = !enablePreFiltering;
      audioDetectionRef.current.setPreFiltering(newValue);
      setEnablePreFiltering(newValue);
    }
  };

  // Handle HPS toggle
  const handleHPSToggle = () => {
    if (audioDetectionRef.current) {
      const newValue = !enableHPS;
      audioDetectionRef.current.setHPS(newValue);
      setEnableHPS(newValue);
    }
  };

  // Handle subharmonic check toggle
  const handleSubharmonicCheckToggle = () => {
    if (audioDetectionRef.current) {
      const newValue = !enableSubharmonicCheck;
      audioDetectionRef.current.setSubharmonicCheck(newValue);
      setEnableSubharmonicCheck(newValue);
    }
  };

  // Handle adaptive threshold toggle
  const handleAdaptiveThresholdToggle = () => {
    if (audioDetectionRef.current) {
      const newValue = !enableAdaptiveThreshold;
      audioDetectionRef.current.setAdaptiveThreshold(newValue);
      setEnableAdaptiveThreshold(newValue);
    }
  };

  // Handle strict harmonics toggle
  const handleStrictHarmonicsToggle = () => {
    if (audioDetectionRef.current) {
      const newValue = !enableStrictHarmonics;
      audioDetectionRef.current.setStrictHarmonics(newValue);
      setEnableStrictHarmonics(newValue);
    }
  };

  // Handle required harmonics count change
  const handleRequiredHarmonicsCountChange = (e) => {
    if (audioDetectionRef.current) {
      const newValue = parseInt(e.target.value, 10);
      audioDetectionRef.current.setRequiredHarmonicsCount(newValue);
      setRequiredHarmonicsCount(newValue);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioDetectionRef.current) {
        audioDetectionRef.current.stop();
      }
    };
  }, []);

  // Handle copy fretboard to clipboard
  const handleCopyFretboard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setCopyStatus('Error: Canvas not found');
      setTimeout(() => setCopyStatus(''), COPY_STATUS_TIMEOUT);
      return;
    }

    // Check for clipboard API support
    if (!navigator.clipboard || !window.ClipboardItem) {
      setCopyStatus('Error: Clipboard not supported');
      setTimeout(() => setCopyStatus(''), COPY_STATUS_TIMEOUT);
      return;
    }

    // Helper to clear status after timeout
    const clearStatusAfterTimeout = (message) => {
      setCopyStatus(message);
      setTimeout(() => setCopyStatus(''), COPY_STATUS_TIMEOUT);
    };

    try {
      // Convert canvas to blob with timeout protection
      const blob = await canvasToBlobWithTimeout(canvas, 'image/png', 5000);

      try {
        // Copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        clearStatusAfterTimeout('Copied!');
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        clearStatusAfterTimeout('Error: Could not copy');
      }
    } catch (err) {
      console.error('Failed to create canvas image:', err);
      clearStatusAfterTimeout('Error: Could not copy');
    }
  };

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    new GuitarApp(ctx,1080,320, key, selScale, notesOrNumbers, sharpsOrFlats,'white', liveMode, detectedNotes);
  }, [selScale,key,sharpsOrFlats,notesOrNumbers,liveMode,detectedNotes])
  

  return (
    <div style={{ display: "flex", flexDirection: "column",gap:"1em"}}>
 
    <div  style={{ display: "flex", flexDirection: "column", alignItems:'center',gap:"1em" }}>
      
      <div style={{ display: "flex", flexDirection:"row", flex:1, justifyContent:'space-evenly', alignItems:'center'}}>
      
        <div className="boxit" >
          <img src={`${process.env.PUBLIC_URL}/Images/wf.icon.png`} alt="Webfretboard Icon" style={{width: "50px", height: "50px"}} />
        </div>
        <div className="boxit" >
        <label htmlFor="keyId" style={{ color: textColor }}>Key:</label>
          <select name="key" id="keyId" className="select-key"
            onChange={(e) => setKey(e.target.value)}>
              <option value="C">C</option>
              <option value="C#">C#</option>
              <option value="D">D</option>
              <option value="D#">D#</option>
              <option value="E">E</option>
              <option value="F">F</option>
              <option value="F#">F#</option>
              <option value="G">G</option>
              <option value="G#">G#</option>
              <option value="A">A</option>
              <option value="A#">A#</option>
              <option value="B">B</option>
          </select>
        </div>  
        <div className="boxit"  >
          <label htmlFor="type" style={{ color: textColor }}>Scale/Chord type:</label>
          <select name="type" id="type"  
              onChange={(e) => setSelScale(e.target.value)}>

              <option value="majorChord">Major Chord</option>
              <option value="majorChord(CAGED)">Major Chord(CAGED)</option>
              <option value="minorChord">Minor Chord</option>
              <option value="minorChord(CAGED)">Minor Chord(CAGED)</option>

              <option value="major7Chord">Major 7th Chord</option>
              <option value="major7Chord(CAGED)">Major 7th Chord(CAGED)</option>
              <option value="minor7Chord">Minor 7th Chord</option>
              <option value="minor7Chord(CAGED)">Minor 7th Chord(CAGED)</option>

              <option value="dom7thChord">Dom 7th Chord</option>

              <option value="minor11Chord">Minor 11th Chord</option>
              <option value="minor11Chord(CAGED)">Minor 11th Chord(CAGED)</option>

              <option value="dimChord">Diminished Chord</option>
              <option value="dim7thChord">Diminished 7th Chord</option>
              <option value="augChord">Augmented Chord</option>
              <option value="hendrixChord">Hendrix Chord</option>

              <option value="majorPent">Major Pent</option>
              <option value="majorPent(CAGED)">Major Pent(CAGED)</option>
              <option value="minorPent">Minor Pent</option>
              <option value="minorPent(CAGED)">Minor Pent(CAGED)</option>
              <option value="dim7thScale">Diminished Scale</option>

              <option value="majorScale">Major Scale</option>
              <option value="majorScale(Targets)">Major Scale(Targets)</option>
              <option value="majorScale(CAGED)">Major Scale(CAGED)</option>
              <option value="blues">Blues Scale</option>
              <option value="blues(CAGED)">Blues Scale(CAGED)</option>
              <option value="bluesMajor">Major Blues Scale</option>
              <option value="bluesMajor(CAGED)">Major Blues Scale(CAGED)</option>

              <option value="minorScale">Minor Scale</option>
              <option value="minorScale(CAGED)">Minor Scale(CAGED)</option>
              <option value="minorHarmonic">Minor Marmonic Scale</option>
              <option value="minorMelUp">Minor Melodic Scale(up)</option>
              <option value="minorMelDown">Minor Melodic Scale(down)</option>

              <option value="ionian">Ionian Mode</option>
              <option value="ionian(CAGED)">Ionian Mode(CAGED)</option>

              <option value="dorian">Dorian Mode</option>
              <option value="dorian(CAGED)">Dorian Mode(CAGED)</option>

              <option value="phrygian">Phrygian Mode</option>
              <option value="phrygian(CAGED)">Phrygian Mode(CAGED)</option>

              <option value="lydian">Lydian Mode</option>
              <option value="lydian(CAGED)">Lydian Mode(CAGED)</option>

              <option value="mixolydian">Mixolydian Mode</option>
              <option value="mixolydian(CAGED)">Mixolydian Mode(CAGED)</option>

              <option value="aeolian">Aeolian Mode</option>
              <option value="aeolian(CAGED)">Aeolian Mode(CAGED)</option>
              <option value="locrian">Locrian Mode</option>
              <option value="mixoBlues">Mixo Blues</option>
              <option value="majorPlusBlues">Major Plus Blues</option>
          </select>
        </div>  
        <div className="boxit" >
          <label htmlFor="notesOrNumbersId" style={{ color: textColor }}>Notes or Intervals:</label>
          <select name="notesOrNumbersName" id="notesOrNumbersId"  
            onChange={(e) => setNotesOrNumbers(e.target.value)}>
            <option value="notes">Notes</option>
            <option value="intervals">Intervals</option>
            <option value="both">Both</option>
          </select>
        </div>
        <div className="boxit" >
            <label htmlFor="sharpsOrFlats" style={{ color: textColor }}>Note(#/b):</label>
            <select name="sharpsOrFlats" id="sharpsOrFlats" className="select-sharp-flat" onChange={(e) => setsharpsOrFlats(e.target.value)}>
                <option value="#">#</option>
                <option value="b">b</option>
            </select>
        </div>
        <div className="boxit" >
            <label htmlFor="bgColor" style={{ color: textColor }}>Background: </label>
            <input 
              type="color" 
              id="bgColor" 
              name="bgColor" 
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              style={{ 
                width: "50px", 
                height: "21px", 
                cursor: "pointer",
                verticalAlign: "middle",
                padding: "0px",
                border: "1px solid rgb(118, 118, 118)",
                borderRadius: "0px"
              }}
            />
        </div>
        <div className="boxit" >
            <label htmlFor="liveMode" style={{ color: textColor }}>Live: </label>
            <button 
              id="liveMode"
              onClick={handleLiveModeToggle}
              style={{ 
                padding: "2px 12px",
                cursor: "pointer",
                backgroundColor: liveMode ? '#ff4444' : '#44ff44',
                color: liveMode ? 'white' : 'black',
                border: "1px solid rgb(118, 118, 118)",
                borderRadius: "3px",
                fontWeight: "bold"
              }}
            >
              {liveMode ? 'ON' : 'OFF'}
            </button>
        </div>
      </div>
      
      <div style={{ display: "flex", flex:1 ,justifyContent:'center', gap: '20px', alignItems: 'center'}}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <canvas ref={canvasRef} width="1080" height="320" />
            <button
              onClick={handleCopyFretboard}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: '1px solid rgb(118, 118, 118)',
                borderRadius: '4px',
                fontWeight: 'bold',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              {copyStatus || 'Copy Fretboard'}
            </button>
          </div>
          {liveMode && (
            <div style={{
              border: `2px solid ${textColor}`,
              borderRadius: '8px',
              padding: '20px',
              minWidth: '200px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                color: textColor,
                fontSize: '18px',
                textAlign: 'center'
              }}>
                Live Detection
              </h3>
              <AudioDeviceSelector 
                audioDetection={audioDetectionRef.current}
                textColor={textColor}
                onDeviceChange={(deviceId) => {
                  console.log('Device changed to:', deviceId);
                }}
              />
              {/* Detection Mode Switch */}
              <div style={{
                marginBottom: '15px',
                padding: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                borderRadius: '4px',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  color: textColor,
                  opacity: 0.8,
                  marginBottom: '5px'
                }}>
                  Detection Mode:
                </div>
                <button 
                  onClick={handleDetectionModeToggle}
                  style={{ 
                    padding: "6px 16px",
                    cursor: "pointer",
                    backgroundColor: detectionMode === 'mono' ? '#4488ff' : '#8844ff',
                    color: 'white',
                    border: "1px solid rgb(118, 118, 118)",
                    borderRadius: "3px",
                    fontWeight: "bold",
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  {detectionMode.toUpperCase()}
                </button>
              </div>
              {/* Advanced Settings */}
              <div style={{
                marginBottom: '15px',
                padding: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                borderRadius: '4px'
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  color: textColor,
                  opacity: 0.8,
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  Advanced Settings:
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '6px' 
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: textColor,
                      opacity: 0.9
                    }}>Pre-Filtering:</span>
                    <button 
                      onClick={handlePreFilteringToggle}
                      style={{ 
                        padding: "3px 10px",
                        cursor: "pointer",
                        backgroundColor: enablePreFiltering ? '#44ff44' : '#666666',
                        color: enablePreFiltering ? 'black' : 'white',
                        border: "1px solid rgb(118, 118, 118)",
                        borderRadius: "3px",
                        fontWeight: "bold",
                        fontSize: '10px',
                        minWidth: '45px'
                      }}
                    >
                      {enablePreFiltering ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: textColor,
                      opacity: 0.9
                    }}>HPS:</span>
                    <button 
                      onClick={handleHPSToggle}
                      style={{ 
                        padding: "3px 10px",
                        cursor: "pointer",
                        backgroundColor: enableHPS ? '#44ff44' : '#666666',
                        color: enableHPS ? 'black' : 'white',
                        border: "1px solid rgb(118, 118, 118)",
                        borderRadius: "3px",
                        fontWeight: "bold",
                        fontSize: '10px',
                        minWidth: '45px'
                      }}
                    >
                      {enableHPS ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: textColor,
                      opacity: 0.9
                    }}>Subharmonic Check:</span>
                    <button 
                      onClick={handleSubharmonicCheckToggle}
                      style={{ 
                        padding: "3px 10px",
                        cursor: "pointer",
                        backgroundColor: enableSubharmonicCheck ? '#44ff44' : '#666666',
                        color: enableSubharmonicCheck ? 'black' : 'white',
                        border: "1px solid rgb(118, 118, 118)",
                        borderRadius: "3px",
                        fontWeight: "bold",
                        fontSize: '10px',
                        minWidth: '45px'
                      }}
                    >
                      {enableSubharmonicCheck ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: textColor,
                      opacity: 0.9
                    }}>Adaptive Threshold:</span>
                    <button 
                      onClick={handleAdaptiveThresholdToggle}
                      style={{ 
                        padding: "3px 10px",
                        cursor: "pointer",
                        backgroundColor: enableAdaptiveThreshold ? '#44ff44' : '#666666',
                        color: enableAdaptiveThreshold ? 'black' : 'white',
                        border: "1px solid rgb(118, 118, 118)",
                        borderRadius: "3px",
                        fontWeight: "bold",
                        fontSize: '10px',
                        minWidth: '45px'
                      }}
                    >
                      {enableAdaptiveThreshold ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: textColor,
                      opacity: 0.9
                    }}>Fifth Validation:</span>
                    <button 
                      onClick={handleFifthValidationToggle}
                      style={{ 
                        padding: "3px 10px",
                        cursor: "pointer",
                        backgroundColor: fifthValidation ? '#44ff44' : '#666666',
                        color: fifthValidation ? 'black' : 'white',
                        border: "1px solid rgb(118, 118, 118)",
                        borderRadius: "3px",
                        fontWeight: "bold",
                        fontSize: '10px',
                        minWidth: '45px'
                      }}
                    >
                      {fifthValidation ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: textColor,
                      opacity: 0.9
                    }}>Strict Harmonics:</span>
                    <button 
                      onClick={handleStrictHarmonicsToggle}
                      style={{ 
                        padding: "3px 10px",
                        cursor: "pointer",
                        backgroundColor: enableStrictHarmonics ? '#44ff44' : '#666666',
                        color: enableStrictHarmonics ? 'black' : 'white',
                        border: "1px solid rgb(118, 118, 118)",
                        borderRadius: "3px",
                        fontWeight: "bold",
                        fontSize: '10px',
                        minWidth: '45px'
                      }}
                    >
                      {enableStrictHarmonics ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: textColor,
                      opacity: 0.9
                    }}>Harmonics Count:</span>
                    <select 
                      value={requiredHarmonicsCount}
                      onChange={handleRequiredHarmonicsCountChange}
                      style={{ 
                        padding: "3px 6px",
                        cursor: "pointer",
                        backgroundColor: '#ffffff',
                        color: 'black',
                        border: "1px solid rgb(118, 118, 118)",
                        borderRadius: "3px",
                        fontWeight: "bold",
                        fontSize: '10px',
                        minWidth: '45px'
                      }}
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                    </select>
                  </div>
                </div>
              </div>
              {audioDeviceLabel && (
                <div style={{
                  marginBottom: '15px',
                  padding: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: textColor,
                  textAlign: 'center',
                  wordBreak: 'break-word'
                }}>
                  <div style={{ opacity: 0.7, marginBottom: '3px' }}>Audio Device:</div>
                  <div style={{ fontWeight: 'bold' }}>{audioDeviceLabel}</div>
                </div>
              )}
              {detectedNotes.length > 0 ? (
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      color: textColor,
                      opacity: 0.8
                    }}>
                      Notes Detected:
                    </div>
                    <div style={{ 
                      fontSize: '28px', 
                      fontWeight: 'bold',
                      color: '#ff00ff',
                      textAlign: 'center'
                    }}>
                      {detectedNotes.map(note => note.fullName).join(', ')}
                    </div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      color: textColor,
                      opacity: 0.8
                    }}>
                      Count:
                    </div>
                    <div style={{ 
                      fontSize: '20px', 
                      fontWeight: 'bold',
                      color: textColor,
                      textAlign: 'center'
                    }}>
                      {detectedNotes.length} note{detectedNotes.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: textColor,
                      opacity: 0.8,
                      marginBottom: '5px'
                    }}>
                      Waveform & Level:
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px', 
                      alignItems: 'center' 
                    }}>
                      <SoundLevelBar 
                        level={volumeLevel} 
                        textColor={textColor}
                        width={30}
                        height={80}
                      />
                      <WaveformDisplay 
                        waveformData={waveformData} 
                        textColor={textColor}
                        width={180}
                        height={80}
                      />
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: textColor,
                      opacity: 0.8,
                      marginTop: '10px',
                      marginBottom: '5px'
                    }}>
                      FFT Spectrum:
                    </div>
                    <FFTScopeDisplay 
                      fftData={fftData} 
                      textColor={textColor}
                      width={220}
                      height={80}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ 
                    color: textColor,
                    textAlign: 'center',
                    opacity: 0.8,
                    fontSize: '14px',
                    marginBottom: '10px'
                  }}>
                    Listening...
                  </div>
                  {frequencyData && Array.isArray(frequencyData) && frequencyData.length > 0 && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ 
                        fontSize: '14px', 
                        color: textColor,
                        opacity: 0.8
                      }}>
                        Detected (low confidence):
                      </div>
                      <div style={{ 
                        fontSize: '20px', 
                        fontWeight: 'bold',
                        color: '#ffaa00',
                        textAlign: 'center',
                        opacity: 0.7
                      }}>
                        {frequencyData.map(f => f.note.fullName).join(', ')}
                      </div>
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    alignItems: 'center' 
                  }}>
                    <SoundLevelBar 
                      level={volumeLevel} 
                      textColor={textColor}
                      width={30}
                      height={80}
                    />
                    <WaveformDisplay 
                      waveformData={waveformData} 
                      textColor={textColor}
                      width={180}
                      height={80}
                    />
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: textColor,
                    opacity: 0.8,
                    marginTop: '10px',
                    marginBottom: '5px'
                  }}>
                    FFT Spectrum:
                  </div>
                  <FFTScopeDisplay 
                    fftData={fftData} 
                    textColor={textColor}
                    width={220}
                    height={80}
                  />
                </div>
              )}
              {/* Device Detection Log */}
              <DeviceDetectionLog 
                logs={deviceLogs} 
                textColor={textColor}
                maxHeight={200}
              />
            </div>
          )}
      </div>
      
      <div style={{ display: "flex", flex:1 ,justifyContent:'center'}}>
          <DescriptionBox selection={selScale} textColor={textColor} />
      </div>
      
      <div style={{ display: "flex", flex:1 ,justifyContent:'center'}}>
          <QRCodeSection textColor={textColor} />
      </div>
      
    </div>
    </div>
  );
}

/*

  const scaleOptions = [
    { label:"Major Chord(CAGED)", value:"majorChord(CAGED)"  },
    { label:"Major Scale", value:"majorScale" },
    { label:"Blues(CAGED)", value:"blues(CAGED)"  }
  ]

  const sharpOrFlatsOptions = [
    { label:"#", value:"#" },
    { label:"b", value:"b" }
  ]

  const notesOrNumbersOptions = [
    { label:"Notes", value:"notes"},
    { label:"Intervals", value:"intervals"},
    { label:"Both", value:"both"  }
  ]

    <FlexView style={{ flex: 3, flexDirection: 'row', width: '100%', height: '100%' }}>
        <BrowserRouter>
        <Link to="/menu1">Menu 1</Link>
        <Link to="/menu2/p01" style={paddingLeft}>Menu 2</Link>
        <Link to="/menu3/p02/456" style={paddingLeft}>Menu 3</Link>
        <br /><br />
        <Routes>
          <Route path="/" element={<Menu1 />} />
          <Route path="/menu1" element={<Menu1 />} />
          <Route path="/menu2/:id" element={<Menu2 />} />
          <Route path="/menu3/:id1/:id2" element={<Menu3 />} />
        </Routes>
        <br /><br />
        Copyright PMK Lab
      </BrowserRouter>
    </FlexView>


   const styles = {
    screen: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: 'yellow',
      color: 'orange'
    },
    text: {
      fontSize: 5,
    },
    picker: {
      marginVertical: 0,
      width: '99%',
      height: '100%',
      padding: 5,
      borderWidth: 15,
      alignItems: "center",
      borderColor: "blue",
      color: 'white',
      backgroundColor: "darkgrey",
      borderLeftColor: "blue",
      borderRadius: 0,
      textAlignVertical: "middle"
    },
    select: {
      marginVertical: 0,
      width: '99%',
      height: '100%',
      padding: 1,
      borderWidth: 1,
      alignItems: "center",
      borderColor: "blue",
      color: 'white',
      backgroundColor: "darkgrey",
      borderLeftColor: "blue",
      borderRadius: 0,
      textAlignVertical: "middle"
    },
  };

  */